// SOW items D, E, F and K — the part-recommendation surfaces.
//
// All read-only except the CCR decision endpoints. The backend applies the bill-of-materials gate and
// every site/permission check; nothing here should re-derive a recommendation client-side.

import { handleRequest } from "../utilities/requestHandler";

const BASE = "/part-recommendations";

/**
 * Item C — companion parts for one part on one machine.
 *
 * Uses the MACHINE-scoped endpoint: the caller knows which machine is open, so the
 * bill-of-materials gate runs at machine level rather than site level. machine_number is required
 * by the API for exactly that reason — the gate fails closed without it.
 */
export const getPartCompanions = ({ productNumber, machineNumber, siteNumber = "", limit = 5 }) =>
    handleRequest("get", `${BASE}/${encodeURIComponent(productNumber)}` +
        `?machine_number=${encodeURIComponent(machineNumber)}` +
        `&site_number=${encodeURIComponent(siteNumber)}&limit=${limit}`);

/** Item D — Critical spares coverage for a site. */
export const getCriticalCoverage = (siteNumber, limit = 20) =>
    handleRequest("get", `${BASE}/coverage/critical?site_number=${encodeURIComponent(siteNumber)}&limit=${limit}`);

/** Item E — what this site is due to reorder, on its own historical rhythm. */
export const getDueForReorder = (siteNumber, limit = 10) =>
    handleRequest("get", `${BASE}/reorder/due?site_number=${encodeURIComponent(siteNumber)}&limit=${limit}`);

// Top parts per machine, ranked by how many OTHER bakeries running the same machine MODEL have
// bought each one. Added 2026-09-03 — the Recommendations tile was site-wide only, so a customer
// could not see which parts matter for a specific machine on their floor.
export const getMachineTopParts = (siteNumber, perMachine = 10) =>
    handleRequest("get", `${BASE}/machines/top-parts?site_number=${encodeURIComponent(siteNumber)}` +
        `&per_machine=${perMachine}`);

/**
 * Item F — the Customer Care review queue.
 *
 * status: "all" (added UAT 2026-08-17, item 4) returns every decision regardless of status, for the
 * admin "Acceptance so far" drill-down. reasonCode filters to one signal type; siteNumber omitted
 * means every site — restricted server-side to super admins.
 */
export const getCcrQueue = ({ siteNumber = null, status = "pending", reasonCode = null, limit = 10, offset = 0 } = {}) =>
    handleRequest("get", `${BASE}/ccr/queue?status=${encodeURIComponent(status)}&limit=${limit}&offset=${offset}` +
        (siteNumber ? `&site_number=${encodeURIComponent(siteNumber)}` : "") +
        (reasonCode ? `&reason_code=${encodeURIComponent(reasonCode)}` : ""));

export const generateCcrQueue = (siteNumber) =>
    handleRequest("post", `${BASE}/ccr/generate`, { site_number: siteNumber });

/** action: "approve" | "reject" | "edit". A rejection requires a note — the API enforces it. */
export const decideCcrItem = (id, action, note = null) =>
    handleRequest("post", `${BASE}/ccr/${id}/decision`, { action, note });

/** siteNumber (added UAT 2026-08-17, item 5): omitted means every site (the admin drill-down's view); */
/** passed means just that site (Recommendations to Review's own, site-scoped view). */
export const getCcrAcceptance = ({ siteNumber = null } = {}) =>
    handleRequest("get", `${BASE}/ccr/acceptance${siteNumber ? `?site_number=${encodeURIComponent(siteNumber)}` : ""}`);

/** Item K — replacement-rate summary. */
export const getWearRateSummary = () => handleRequest("get", `${BASE}/wear-rate?format=json`);

/**
 * Item K — the CSV download, authenticated.
 *
 * BUG FIX 2026-08-14 (UAT item K): this used to be a plain `<a href>` straight to the API URL. This
 * app has no cookie session — the JWT lives in localStorage and is only ever attached by customHttp's
 * request interceptor — so a raw browser navigation carried no Authorization header at all and always
 * came back "Authorization required". Going through handleRequest (customHttp) instead attaches the
 * token like every other authenticated call in this app; responseType "blob" gets the raw file bytes
 * back instead of trying to JSON-parse a CSV.
 */
export const downloadWearRateCsv = ({ confidence = null, minCustomers = null } = {}) =>
    handleRequest("get", `${BASE}/wear-rate`, null, {
        params: {
            format: "csv",
            ...(confidence ? { confidence } : {}),
            ...(minCustomers ? { min_customers: minCustomers } : {}),
        },
        responseType: "blob",
    });
